import { useState } from "react";
import { motion } from "framer-motion";
import { Check, MessageSquare, PartyPopper, ThumbsUp, Youtube } from "lucide-react";
import { toast } from "sonner";
import { CHANNEL_HANDLE, SHORT_URL, SUBSCRIBE_URL } from "@/constants/campaign";

const STORAGE_KEY = "fxtm-gold-drop-steps";

const STEPS = [
  {
    key: "subscribed",
    number: "01",
    icon: Youtube,
    title: `Subscribe to ${CHANNEL_HANDLE}`,
    instruction: "Open the official FXTM Africa YouTube channel and hit SUBSCRIBE + the bell icon so you never miss the winner announcement.",
    cta: "Subscribe on YouTube",
    link: SUBSCRIBE_URL,
    testid: "step-1-subscribe-btn",
  },
  {
    key: "liked",
    number: "02",
    icon: ThumbsUp,
    title: "Like the Giveaway Video",
    instruction: "Open the featured YouTube Short (or any video on the channel) and give it a thumbs up to lock in your entry.",
    cta: "Like the Video",
    link: SHORT_URL,
    testid: "step-2-like-btn",
  },
  {
    key: "commented",
    number: "03",
    icon: MessageSquare,
    title: "Drop Your Response Comment",
    instruction: "Comment on the video telling us your trading goal for 2026. Winners are picked randomly from the video comments!",
    cta: "Leave Your Comment",
    link: SHORT_URL,
    testid: "step-3-comment-btn",
  },
];

export default function Steps() {
  const [done, setDone] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
  });

  const toggle = (key) => {
    const next = { ...done, [key]: !done[key] };
    setDone(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    if (STEPS.every((s) => next[s.key])) {
      toast.success("All 3 steps complete — you're in the $500 draw! Winners announced live on the channel.");
    }
  };

  const allDone = STEPS.every((s) => done[s.key]);

  return (
    <section data-testid="entry-steps-section" className="relative px-4 py-24 sm:px-6">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="mb-14 max-w-2xl"
        >
          <p className="mb-3 text-xs font-mono font-bold uppercase tracking-[0.3em] text-red-500">How To Enter</p>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
            3 Simple Steps to Enter the $500 Draw
          </h2>
          <p className="mt-4 text-base text-slate-500 sm:text-lg">
            No sign-up form. No deposit. Everything happens directly on our official YouTube channel.
          </p>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-3">
          {STEPS.map((step, i) => {
            const Icon = step.icon;
            const isDone = !!done[step.key];
            return (
              <motion.div
                key={step.key}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.15 }}
                whileHover={{ y: -6 }}
                className={`relative flex flex-col rounded-2xl border p-7 transition-colors duration-300 ${
                  isDone
                    ? "border-emerald-500/50 bg-[#F0FDF4] shadow-[0_0_25px_rgba(0,230,118,0.1)]"
                    : "border-red-600/30 bg-[#FFFFFF] shadow-[0_0_25px_rgba(255,0,0,0.1)] hover:border-red-500/70"
                }`}
              >
                <div className="mb-6 flex items-center justify-between">
                  <span className="font-timer text-4xl font-bold text-red-600/40">{step.number}</span>
                  <span className={`flex h-12 w-12 items-center justify-center rounded-xl ${isDone ? "bg-emerald-500/15 text-emerald-400" : "bg-red-600/15 text-red-500"}`}>
                    <Icon className="h-6 w-6" />
                  </span>
                </div>
                <h3 className="font-display text-xl font-semibold text-slate-900 sm:text-2xl">{step.title}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-500">{step.instruction}</p>
                <a
                  data-testid={step.testid}
                  href={step.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => toast.success(`Opening YouTube — complete step ${step.number}!`)}
                  className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-red-600 py-3.5 text-sm font-bold text-white shadow-[0_0_20px_rgba(255,0,0,0.35)] transition-all duration-200 hover:scale-[1.02] hover:bg-red-700 active:scale-[0.98]"
                >
                  <Youtube className="h-4 w-4" />
                  {step.number}. {step.cta}
                </a>
                <button
                  data-testid={`step-${step.key}-checkbox`}
                  onClick={() => toggle(step.key)}
                  className={`mt-3 flex items-center justify-center gap-2 rounded-xl border py-3 text-sm font-bold transition-all duration-200 ${
                    isDone
                      ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-400"
                      : "border-slate-300 text-slate-500 hover:border-red-600/50 hover:text-red-500"
                  }`}
                >
                  <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${isDone ? "border-emerald-400 bg-emerald-400 text-white" : "border-slate-400"}`}>
                    {isDone && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                  </span>
                  {isDone ? "Done — step complete" : "Mark as done"}
                </button>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          data-testid="checklist-tracker-box"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className={`mt-8 flex items-center justify-center gap-3 rounded-2xl border px-6 py-5 text-center font-bold ${
            allDone
              ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400"
              : "border-slate-200 bg-[#F4F4F5] text-slate-500"
          }`}
        >
          {allDone ? (
            <>
              <PartyPopper className="h-5 w-5" />
              Entry locked in! Watch the channel — 10 random winners announced when the timer hits zero.
            </>
          ) : (
            <>Your progress: {STEPS.filter((s) => done[s.key]).length}/3 steps completed — saved on this device.</>
          )}
        </motion.div>
      </div>
    </section>
  );
}
